import { useState, useEffect } from 'react';
import { useLocation } from "wouter";
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Nav from 'react-bootstrap/Nav';
import Row from 'react-bootstrap/Row';
import Tab from 'react-bootstrap/Tab';
import AlertNotification, { IAlert } from "components/common/AlertNotification/AlertNotification";
import apiService from 'Services/apiService';

import './style.css';

type ErrorState = {
  actualPassword: string;
  newPassword: string;
  repNewPassword: string;
};

type PasswordStatus = {
  length: boolean;
  capitalLetter: boolean;
  lowerCase: boolean;
  number: boolean;
  specialCharacter: boolean;
  samePasswords: boolean;
};

export const Perfil = () => {
  const [alert, setAlert] = useState<IAlert>({ show: false, title: '', details: '', variant: 'Success' });
  const [actualPassword, setActualPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repNewPassword, setRepNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] =  useState<string>("general");
  const [location, setLocation] = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const tabParam = queryParams.get("op");
    if (tabParam === "pW2c") {
      setActiveTab("password");
      setAlert({
        show: true,
        title: 'Cambio de contraseña requerido',
        details: 'Debes actualizar tu contraseña para continuar utilizando el sistema.',
        variant: 'Warning'
      });
    }
  }, [location]);
  
  const [errors, setErrors] = useState<ErrorState>({
    actualPassword: "",
    newPassword: "",
    repNewPassword: "",
  });

  const [statusNewPwd, setStatusNewPwd] = useState<PasswordStatus>({
    length: false,
    capitalLetter: false,
    lowerCase: false,
    number: false,
    specialCharacter: false,
    samePasswords: false,
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user?.user?._fullName || null;
  const userEmail = user?.user?._email || null;
  const userID = user?.user?._idUsuario || null;

  useEffect(() => {
    const length = newPassword.length >= 10;
    const capitalLetter = /[A-Z]/.test(newPassword);
    const lowerCase = /[a-z]/.test(newPassword);
    const number = /\d/.test(newPassword);
    const specialCharacter = /[!@#$%^&*]/.test(newPassword);
    const samePasswords = !!newPassword && newPassword === repNewPassword;

    setStatusNewPwd({
      length,
      capitalLetter,
      lowerCase,
      number,
      specialCharacter,
      samePasswords,
    });
  }, [newPassword, repNewPassword]);

  const validateForm = () => {
    const newErrors: ErrorState = {
      actualPassword: "",
      newPassword: "",
      repNewPassword: ""
    };

    if (!actualPassword) {
      newErrors.actualPassword = "La contraseña actual es requerida";
    }
    if (!newPassword) {
      newErrors.newPassword = "La nueva contraseña es requerida";
    } else if (newPassword === actualPassword) {
      newErrors.newPassword = "La nueva contraseña no puede ser igual a la actual";
    }
    if (!repNewPassword) {
      newErrors.repNewPassword = "La confirmación de la contraseña es requerida";
    } else if (repNewPassword !== newPassword) {
      newErrors.repNewPassword = "La confirmación debe coincidir con la nueva contraseña";
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "") &&
           Object.values(statusNewPwd).every((status) => status === true);
  };

  const updatePass = (event: React.FormEvent) => {
    event.preventDefault();
    if (validateForm()) {
      setLoading(true);
      apiService({
        name: 'updatePassword',
        queryParams: { idUsuario: userID, claveActual: actualPassword, claveNueva: newPassword },
        useSpecialToken: true,
        onSuccess: () => {
          // Actualizar la propiedad _changePassword en localStorage
          const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
          if (storedUser?.user) {
            storedUser.user._changePassword = false;
            localStorage.setItem("user", JSON.stringify(storedUser));
          }

          setLocation('/')
          
          setAlert({
            show: true,
            title: 'Éxito!',
            details: 'Contraseña actualizada correctamente',
            variant: 'Success'
          });
        },
        onError: (error) => {
          setAlert({
            show: true,
            title: 'Error al realizar la acción.',
            details: error?.message || "Ocurrió un error inesperado",
            variant: 'Danger'
          });
        },
        onFinally() {
          setLoading(false);
        }
      });
    }
  };

  const resetAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  return (
    <>
      <Tab.Container id="left-tabs-example" activeKey={activeTab} onSelect={(k) => setActiveTab(k || "general")}>
        <Row className="m-3">
          <Col sm={12} className="mb-3">
            <Row>
              <Col sm={3}></Col> 
              <Col sm={9}>
                <h5>Mi perfil</h5>
                <small>Aquí puedes ver la información básica de tu perfil y realizar cambios en tu contraseña.</small>
              </Col>
            </Row>
          </Col>
          <Col sm={3}>
            <Nav variant="pills" className="flex-column">
              <Nav.Item>
                <Nav.Link eventKey="general" className="perfil-tag"><i className="bi bi-person-gear"></i> General</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="password" className="perfil-tag"><i className="bi bi-lock"></i> Cambiar contraseña</Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
          <Col sm={9} className="perfil-detail-container">
            <Tab.Content>
              <Tab.Pane eventKey="general">
                  <div className="p-3">
                    <div className='text-center text-md-start'>
                      <div className="p-2">
                        <Form>
                          <fieldset disabled className="d-flex">
                            <Form.Group className="mb-3 col-4 mx-2">
                              <Form.Label htmlFor="name">Nombre</Form.Label>
                              <Form.Control id="name" placeholder={ userName || "Nombre"} />
                            </Form.Group>
                            <Form.Group className="mb-3 col-4 mx-2">
                              <Form.Label htmlFor="email">Correo</Form.Label>
                              <Form.Control id="email" placeholder={userEmail || "Correo"} />
                            </Form.Group>
                          </fieldset>
                        </Form>
                      </div>
                    </div>
                  </div>
                </Tab.Pane>
              <Tab.Pane eventKey="password">
                <div className="p-3">
                  <div className="p-2">
                    <h6 className="mb-3"><b>Cambiar contraseña</b></h6>
                    <Form onSubmit={updatePass}>
                      <Form.Group className="mb-3 col-4">
                        <Form.Label htmlFor="actualPassword">Contraseña actual</Form.Label>
                        <Form.Control
                          type="password"
                          id="actualPassword"
                          placeholder="Contraseña actual"
                          value={actualPassword}
                          onChange={(e) => setActualPassword(e.target.value)}
                          isInvalid={!!errors.actualPassword}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.actualPassword}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <h6 className="mt-4"><b><small>La nueva contraseña debe cumplir:</small></b></h6>
                      <ul>
                        <li className={statusNewPwd.length ? 'text-success' : ''}><small>Longitud mínima de 10 caracteres {statusNewPwd.length && <i className="bi bi-check-circle-fill"></i>}</small></li>
                        <li className={statusNewPwd.capitalLetter ? 'text-success' : ''}><small>Al menos una letra mayúscula {statusNewPwd.capitalLetter && <i className="bi bi-check-circle-fill"></i>}</small></li>
                        <li className={statusNewPwd.lowerCase ? 'text-success' : ''}><small>Al menos una letra minúscula {statusNewPwd.lowerCase && <i className="bi bi-check-circle-fill"></i>}</small></li>
                        <li className={statusNewPwd.number ? 'text-success' : ''}><small>Al menos un número {statusNewPwd.number && <i className="bi bi-check-circle-fill"></i>}</small></li>
                        <li className={statusNewPwd.specialCharacter ? 'text-success' : ''}><small>Al menos un caracter especial (!@#$%^&*) {statusNewPwd.specialCharacter && <i className="bi bi-check-circle-fill"></i>}</small></li>
                        <li className={statusNewPwd.samePasswords ? 'text-success' : ''}><small>Las nuevas contraseñas deben coincidir {statusNewPwd.samePasswords && <i className="bi bi-check-circle-fill"></i>}</small></li>
                      </ul>
                      <div className="d-flex">
                        <Form.Group className="mb-3 col-4">
                          <Form.Label htmlFor="newPassword">Nueva contraseña</Form.Label>
                          <Form.Control
                            type="password"
                            id="newPassword"
                            placeholder="Nueva contraseña"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            isInvalid={!!errors.newPassword}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.newPassword}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3 col-4 mx-2">
                          <Form.Label htmlFor="RepNewPassword">Confirmar nueva contraseña</Form.Label>
                          <Form.Control
                            type="password"
                            id="RepNewPassword"
                            placeholder="Confirmar nueva contraseña"
                            value={repNewPassword}
                            onChange={(e) => setRepNewPassword(e.target.value)}
                            isInvalid={!!errors.repNewPassword}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.repNewPassword}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>
                      <div className="my-3">
                        <button className={`primary-bg-btn py-2 px-3 ${(loading || !Object.values(statusNewPwd).every(status => status)) ? 'disabled-btn' : ''}`} type="submit" disabled={loading}>
                          {loading && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>} Actualizar
                        </button>
                      </div>
                    </Form>
                  </div>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
      <AlertNotification
        show={alert?.show}
        title={alert?.title}
        details={alert?.details}
        variant={alert?.variant}
        onClose={resetAlert}
      />
    </>
  );
};
